// =========================================================
// ARMONIA COLLECTION CARD GALLERY — V7
// - direct custom-element lifecycle: survives Shopify section/facet re-renders
// - native horizontal swipe + CSS scroll snap on mobile
// - desktop arrows loop across every image
// - rapid clicks keep an independent target index
// - non-visible images are hydrated only when needed
// =========================================================

if (!customElements.get('armonia-card-gallery')) {
  customElements.define(
    'armonia-card-gallery',
    class ArmoniaCardGallery extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.bootAttempts = 0;
        this.boot();
      }

      disconnectedCallback() {
        if (this.bootFrame) cancelAnimationFrame(this.bootFrame);
        if (this.settleTimer) window.clearTimeout(this.settleTimer);
        this.controller?.abort();
        this.ready = false;
        this.removeAttribute('data-gallery-ready');
      }

      boot() {
        if (!this.isConnected || this.ready) return;

        const viewport = this.querySelector('[data-armonia-gallery-viewport]');
        const slides = Array.from(this.querySelectorAll('[data-armonia-gallery-slide]'));
        const prev = this.querySelector('[data-armonia-gallery-prev]');
        const next = this.querySelector('[data-armonia-gallery-next]');

        if (!viewport || slides.length < 2 || !prev || !next) {
          this.bootAttempts += 1;
          if (this.bootAttempts < 60) {
            this.bootFrame = requestAnimationFrame(() => this.boot());
          }
          return;
        }

        this.viewport = viewport;
        this.slides = slides;
        this.prevButton = prev;
        this.nextButton = next;
        this.currentIndex = this.indexFromScroll();
        this.targetIndex = this.currentIndex;
        this.programmaticIndex = null;
        this.suppressLinkUntil = 0;
        this.pointerStart = null;

        this.controller?.abort();
        this.controller = new AbortController();
        const { signal } = this.controller;

        this.prevButton.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.step(-1);
          },
          { signal },
        );

        this.nextButton.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.step(1);
          },
          { signal },
        );

        this.viewport.addEventListener('scroll', () => this.onScroll(), {
          passive: true,
          signal,
        });

        if ('onscrollend' in this.viewport) {
          this.viewport.addEventListener('scrollend', () => this.finalizeScroll(), {
            passive: true,
            signal,
          });
        }

        this.viewport.addEventListener(
          'pointerdown',
          (event) => {
            if (event.pointerType === 'mouse' && event.button !== 0) return;

            this.programmaticIndex = null;
            const index = this.indexFromScroll();
            this.currentIndex = index;
            this.targetIndex = index;
            this.pointerStart = {
              x: event.clientX,
              y: event.clientY,
              id: event.pointerId,
              scrollLeft: this.viewport.scrollLeft,
            };

            // Make the current + next slide ready before the finger reaches it.
            // We deliberately do not preload the previous slide here to keep grid image cost low.
            this.hydrateSlide(index);
            this.hydrateSlide((index + 1) % this.count);
          },
          { passive: true, signal },
        );

        this.viewport.addEventListener(
          'pointermove',
          (event) => {
            if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return;

            const dx = event.clientX - this.pointerStart.x;
            const dy = event.clientY - this.pointerStart.y;

            if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
              this.suppressLinkUntil = Date.now() + 600;
            }
          },
          { passive: true, signal },
        );

        const endPointer = () => {
          if (
            this.pointerStart &&
            Math.abs(this.viewport.scrollLeft - this.pointerStart.scrollLeft) > 5
          ) {
            this.suppressLinkUntil = Date.now() + 600;
          }
          this.pointerStart = null;
        };

        this.viewport.addEventListener('pointerup', endPointer, { passive: true, signal });
        this.viewport.addEventListener('pointercancel', endPointer, { passive: true, signal });

        // A real tap opens the product; a swipe must never create an accidental navigation.
        this.viewport.addEventListener(
          'click',
          (event) => {
            if (Date.now() >= this.suppressLinkUntil) return;

            const link = event.target.closest('[data-armonia-gallery-product-link]');
            if (!link) return;

            event.preventDefault();
            event.stopPropagation();
          },
          { capture: true, signal },
        );

        // Desktop intent: prepare only the next image, not the whole gallery.
        this.addEventListener(
          'mouseenter',
          () => this.hydrateSlide((this.targetIndex + 1) % this.count),
          { passive: true, signal },
        );

        this.addEventListener(
          'focusin',
          () => this.hydrateSlide((this.targetIndex + 1) % this.count),
          { passive: true, signal },
        );

        this.ready = true;
        this.setAttribute('data-gallery-ready', 'true');
        this.setIndexState(this.currentIndex);
        this.hydrateSlide(this.currentIndex);
      }

      get count() {
        return this.slides?.length || 0;
      }

      indexFromScroll() {
        if (!this.viewport || !this.count) return 0;
        const width = this.viewport.clientWidth;
        if (!width) return 0;

        return Math.max(
          0,
          Math.min(this.count - 1, Math.round(this.viewport.scrollLeft / width)),
        );
      }

      step(direction) {
        if (!this.count) return false;

        // IMPORTANT: targetIndex is independent from in-progress smooth-scroll events.
        // Rapid clicks therefore become 1→2→3→4, instead of being reset mid-animation.
        const from = Number.isInteger(this.targetIndex)
          ? this.targetIndex
          : this.currentIndex || 0;

        const next = (from + direction + this.count) % this.count;
        const wrapped =
          (direction > 0 && from === this.count - 1) ||
          (direction < 0 && from === 0);

        return this.goTo(next, wrapped ? 'auto' : this.motionBehavior());
      }

      goTo(index, behavior = this.motionBehavior()) {
        if (!this.viewport || !this.count) return false;

        const width = this.viewport.clientWidth;
        if (!width) return false;

        const normalized = ((Number(index) % this.count) + this.count) % this.count;

        this.targetIndex = normalized;
        this.programmaticIndex = normalized;
        this.hydrateSlide(normalized);
        this.hydrateSlide((normalized + 1) % this.count);
        this.setIndexState(normalized);

        const left = normalized * width;

        if (behavior === 'auto') {
          this.viewport.scrollTo({ left, top: 0, behavior: 'auto' });
          this.currentIndex = normalized;
          this.programmaticIndex = null;
          this.setIndexState(normalized);
        } else {
          this.viewport.scrollTo({ left, top: 0, behavior });
          this.scheduleSettle();
        }

        return true;
      }

      motionBehavior() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth';
      }

      onScroll() {
        const visible = this.indexFromScroll();
        this.hydrateSlide(visible);
        this.hydrateSlide((visible + 1) % this.count);

        if (
          this.pointerStart &&
          Math.abs(this.viewport.scrollLeft - this.pointerStart.scrollLeft) > 5
        ) {
          this.suppressLinkUntil = Date.now() + 600;
        }

        this.scheduleSettle();
      }

      scheduleSettle() {
        window.clearTimeout(this.settleTimer);
        this.settleTimer = window.setTimeout(() => this.finalizeScroll(), 140);
      }

      finalizeScroll() {
        window.clearTimeout(this.settleTimer);

        const actual = this.indexFromScroll();

        if (this.programmaticIndex !== null) {
          const width = this.viewport.clientWidth || 1;
          const expected = this.programmaticIndex * width;

          if (Math.abs(this.viewport.scrollLeft - expected) <= 2) {
            this.currentIndex = this.programmaticIndex;
          } else {
            this.currentIndex = actual;
          }

          this.targetIndex = this.currentIndex;
          this.programmaticIndex = null;
        } else {
          this.currentIndex = actual;
          this.targetIndex = actual;
        }

        this.setIndexState(this.currentIndex);
        this.hydrateSlide(this.currentIndex);
        this.hydrateSlide((this.currentIndex + 1) % this.count);
      }

      setIndexState(index) {
        this.dataset.galleryIndex = String(index);

        this.slides?.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
          slide.tabIndex = active ? 0 : -1;
        });

        const status = this.querySelector('[data-armonia-gallery-status]');
        if (status) status.textContent = `${index + 1} / ${this.count}`;
      }

      hydrateSlide(index) {
        const slide = this.slides?.[index];
        if (!slide || slide.dataset.hydrated === 'true') return;

        const template = slide.querySelector('template[data-armonia-image-template]');

        if (!template) {
          slide.dataset.hydrated = 'true';
          return;
        }

        slide.appendChild(template.content.cloneNode(true));
        template.remove();
        slide.dataset.hydrated = 'true';
      }

      hydrateAround(index) {
        if (!this.count) return;

        const normalized = ((Number(index) % this.count) + this.count) % this.count;
        this.hydrateSlide(normalized);
        this.hydrateSlide((normalized + 1) % this.count);

        if (this.count > 2) {
          this.hydrateSlide((normalized - 1 + this.count) % this.count);
        }
      }

      showMediaById(mediaId) {
        if (mediaId === null || mediaId === undefined || !this.count) return false;

        const index = this.slides.findIndex(
          (slide) => String(slide.dataset.mediaId || '') === String(mediaId),
        );

        if (index < 0) return false;
        return this.goTo(index, 'auto');
      }
    },
  );
}

if (!customElements.get('armonia-card-purchase')) {
  customElements.define(
    'armonia-card-purchase',
    class ArmoniaCardPurchase extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.addButton = this.querySelector('[data-armonia-add]');
        this.addButtonText = this.querySelector('[data-armonia-add-text]');
        this.errorMessage = this.querySelector('[data-armonia-error]');
        this.variantData = this.querySelector('[data-armonia-variants]');

        if (!this.addButton || !this.variantData) return;

        try {
          this.variants = JSON.parse(this.variantData.textContent || '[]');
        } catch (error) {
          console.error('Armonia card variants could not be parsed.', error);
          this.disableWithMessage('Pogledajte proizvod');
          return;
        }

        this.optionCount = Number(this.dataset.optionCount || 0);
        this.hasChoices = this.dataset.hasChoices === 'true';
        this.productAvailable = this.dataset.productAvailable === 'true';

        this.selectedValues = new Array(this.optionCount).fill('');
        this.selectedVariant = null;

        this.card = this.closest('.card');
        this.priceZone = this.card?.querySelector('[data-armonia-price-zone]');
        this.basePrice = this.priceZone?.querySelector('[data-armonia-base-price]');
        this.variantPrice = this.priceZone?.querySelector('[data-armonia-variant-price]');
        this.currentPrice = this.priceZone?.querySelector('[data-armonia-current-price]');
        this.comparePrice = this.priceZone?.querySelector('[data-armonia-compare-price]');

        this.optionGroups = Array.from(this.querySelectorAll('[data-option-group]'));
        this.colorOptionIndex = this.getColorOptionIndex();
        this.gallery = this.closest('.armonia-card-wrapper')?.querySelector('armonia-card-gallery') || null;

        this.bindOptionControls();
        this.addButton.addEventListener('click', this.handleAddToCart.bind(this));

        const availableVariants = this.variants.filter((variant) => variant.available);

        // Always refresh meaningful option controls, even on a fully sold-out product,
        // so customers can still see which sizes/options exist and which are unavailable.
        if (this.hasChoices) {
          this.refreshControls();
        }

        if (!this.productAvailable || availableVariants.length === 0) {
          this.setButtonState('sold-out');
          return;
        }

        if (this.hasChoices) {
          // If only one purchasable combination remains, show it explicitly and select it
          // automatically. The customer sees exactly what will be added to cart, while
          // sold-out alternatives remain visible but disabled.
          if (availableVariants.length === 1) {
            this.selectedValues = availableVariants[0].options.slice(0, this.optionCount);
            this.refreshControls();
            this.updateSelectedVariant();
            this.syncGalleryToSelection();
          } else {
            this.updateSelectedVariant();
          }
        } else {
          this.selectedVariant = availableVariants[0] || this.variants[0] || null;
          this.setButtonState(this.selectedVariant?.available ? 'ready' : 'sold-out');
        }
      }


      getColorOptionIndex() {
        const colorGroup = this.optionGroups.find((group) => {
          const label = group.querySelector('.armonia-card-option__label')?.textContent || '';
          const normalized = label.trim().toLowerCase();
          return normalized === 'boja' || normalized === 'color' || normalized === 'colour';
        });

        return colorGroup ? Number(colorGroup.dataset.optionIndex) : -1;
      }

      bindOptionControls() {
        this.optionGroups.forEach((group) => {
          const index = Number(group.dataset.optionIndex);
          const select = group.querySelector('[data-armonia-select]');
          const pills = Array.from(group.querySelectorAll('[data-option-value]'));

          if (select) {
            select.addEventListener('change', (event) => {
              this.handleSelection(index, event.target.value);
            });
          }

          pills.forEach((pill) => {
            pill.addEventListener('click', () => {
              if (pill.disabled) return;
              this.handleSelection(index, pill.dataset.optionValue || '');
            });
          });
        });
      }

      handleSelection(index, value) {
        this.clearError();

        this.selectedValues[index] = value;

        for (let i = index + 1; i < this.selectedValues.length; i += 1) {
          this.selectedValues[i] = '';
        }

        this.refreshControls();
        this.updateSelectedVariant();
        this.syncGalleryToSelection();
      }


      syncGalleryToSelection() {
        if (!this.gallery || this.colorOptionIndex < 0) return;

        const selectedColor = this.selectedValues[this.colorOptionIndex];
        if (!selectedColor) return;

        let mediaId = this.selectedVariant?.featuredMediaId || null;

        if (!mediaId) {
          const candidates = this.getMatchingAvailableVariants().filter((variant) => variant.featuredMediaId);
          const uniqueMediaIds = [...new Set(candidates.map((variant) => String(variant.featuredMediaId)))];
          if (uniqueMediaIds.length === 1) mediaId = uniqueMediaIds[0];
        }

        if (mediaId && typeof this.gallery.showMediaById === 'function') {
          this.gallery.showMediaById(mediaId);
        }
      }

      refreshControls() {
        for (let index = 0; index < this.optionCount; index += 1) {
          const previousSelectionsReady =
            index === 0 || this.selectedValues.slice(0, index).every((value) => Boolean(value));

          if (!previousSelectionsReady) {
            this.selectedValues[index] = '';
            this.setGroupState(index, new Set(), true);
            continue;
          }

          const availableValues = this.getAvailableValues(index);

          if (this.selectedValues[index] && !availableValues.has(this.selectedValues[index])) {
            for (let resetIndex = index; resetIndex < this.selectedValues.length; resetIndex += 1) {
              this.selectedValues[resetIndex] = '';
            }
          }

          this.setGroupState(index, availableValues, false);
        }

        this.syncControlValues();
      }

      getAvailableValues(index) {
        const prefix = this.selectedValues.slice(0, index);
        const values = new Set();

        this.variants.forEach((variant) => {
          if (!variant.available) return;

          const matchesPrefix = prefix.every((value, prefixIndex) => {
            return !value || variant.options[prefixIndex] === value;
          });

          if (matchesPrefix && variant.options[index] !== undefined) {
            values.add(variant.options[index]);
          }
        });

        return values;
      }

      setGroupState(index, availableValues, disabledBySequence) {
        const group = this.optionGroups.find((item) => Number(item.dataset.optionIndex) === index);
        if (!group) return;

        group.classList.toggle('is-disabled', disabledBySequence);

        const select = group.querySelector('[data-armonia-select]');
        if (select) {
          select.disabled = disabledBySequence;

          Array.from(select.options).forEach((option) => {
            if (!option.value) {
              option.disabled = false;
              return;
            }

            const unavailable = !disabledBySequence && !availableValues.has(option.value);
            const baseLabel = option.dataset.baseLabel || option.textContent.trim();

            option.dataset.baseLabel = baseLabel;
            option.disabled = disabledBySequence || unavailable;
            option.textContent = unavailable ? `${baseLabel} — nema na stanju` : baseLabel;
          });
        }

        group.querySelectorAll('[data-option-value]').forEach((pill) => {
          const value = pill.dataset.optionValue || '';
          const unavailable = !disabledBySequence && !availableValues.has(value);
          const disabled = disabledBySequence || unavailable;
          const baseLabel = pill.dataset.optionLabel || value;

          pill.disabled = disabled;
          pill.classList.toggle('is-unavailable', unavailable);
          pill.classList.toggle('is-waiting', disabledBySequence);
          pill.setAttribute('aria-label', unavailable ? `${baseLabel} — nema na stanju` : baseLabel);
        });
      }

      syncControlValues() {
        this.optionGroups.forEach((group) => {
          const index = Number(group.dataset.optionIndex);
          const selected = this.selectedValues[index] || '';
          const select = group.querySelector('[data-armonia-select]');

          if (select && select.value !== selected) {
            select.value = selected;
          }

          group.querySelectorAll('[data-option-value]').forEach((pill) => {
            const isSelected = selected && pill.dataset.optionValue === selected;

            pill.classList.toggle('is-selected', Boolean(isSelected));
            pill.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          });
        });
      }

      updateSelectedVariant() {
        const selectionComplete =
          this.optionCount === 0 || this.selectedValues.every((value) => Boolean(value));

        if (!selectionComplete) {
          this.selectedVariant = null;
          this.updatePriceFromCurrentSelection();
          this.setButtonState('choose');
          return;
        }

        this.selectedVariant =
          this.variants.find((variant) =>
            variant.options.every((optionValue, index) => optionValue === this.selectedValues[index]),
          ) || null;

        if (!this.selectedVariant || !this.selectedVariant.available) {
          this.updatePriceFromCurrentSelection();
          this.setButtonState('sold-out');
          return;
        }

        this.showVariantPrice(this.selectedVariant);
        this.setButtonState('ready');
      }

      getMatchingAvailableVariants() {
        return this.variants.filter((variant) => {
          if (!variant.available) return false;

          return this.selectedValues.every((selectedValue, index) => {
            return !selectedValue || variant.options[index] === selectedValue;
          });
        });
      }

      updatePriceFromCurrentSelection() {
        const hasAnySelection = this.selectedValues.some((value) => Boolean(value));

        if (!hasAnySelection) {
          this.restoreBasePrice();
          return;
        }

        const candidates = this.getMatchingAvailableVariants();

        if (!candidates.length) {
          this.restoreBasePrice();
          return;
        }

        const numericCandidates = candidates
          .map((variant) => ({
            variant,
            amount: Number(variant.priceAmount),
          }))
          .filter((item) => Number.isFinite(item.amount));

        if (numericCandidates.length === candidates.length) {
          const amounts = numericCandidates.map((item) => item.amount);
          const minAmount = Math.min(...amounts);
          const maxAmount = Math.max(...amounts);

          if (minAmount === maxAmount) {
            this.showPartialExactPrice(candidates);
            return;
          }

          const lowest = numericCandidates.find((item) => item.amount === minAmount)?.variant;

          if (lowest?.price) {
            this.showPriceText(`Od ${lowest.price}`);
            return;
          }
        }

        const uniqueFormattedPrices = new Set(
          candidates.map((variant) => variant.price).filter(Boolean),
        );

        if (uniqueFormattedPrices.size === 1) {
          this.showPartialExactPrice(candidates);
          return;
        }

        this.restoreBasePrice();
      }

      showPartialExactPrice(candidates) {
        const representative = candidates[0];
        if (!representative) {
          this.restoreBasePrice();
          return;
        }

        const compareValues = new Set(
          candidates.map((variant) => variant.compareAtPrice || ''),
        );
        const allOnSale = candidates.every((variant) => Boolean(variant.onSale));

        this.showPriceText(
          representative.price || '',
          allOnSale && compareValues.size === 1 ? representative.compareAtPrice : '',
        );
      }

      restoreBasePrice() {
        if (this.basePrice) this.basePrice.hidden = false;
        if (this.variantPrice) this.variantPrice.hidden = true;
      }

      showPriceText(priceText, compareText = '') {
        if (!this.variantPrice || !this.currentPrice) return;

        if (this.basePrice) this.basePrice.hidden = true;

        this.currentPrice.textContent = priceText || '';

        if (this.comparePrice) {
          const showCompare = Boolean(compareText);
          this.comparePrice.hidden = !showCompare;
          this.comparePrice.textContent = showCompare ? compareText : '';
        }

        this.variantPrice.hidden = false;
      }

      showVariantPrice(variant) {
        this.showPriceText(
          variant.price || '',
          variant.onSale && variant.compareAtPrice ? variant.compareAtPrice : '',
        );
      }

      setButtonState(state) {
        if (!this.addButton || !this.addButtonText) return;

        this.addButton.classList.remove('is-added', 'is-loading');

        if (state === 'ready') {
          this.addButton.disabled = false;
          this.addButtonText.textContent = 'Dodaj u korpu';
          return;
        }

        this.addButton.disabled = true;

        if (state === 'sold-out') {
          this.addButtonText.textContent = 'Nema na stanju';
        } else {
          this.addButtonText.textContent = this.optionCount > 1 ? 'Izaberite opcije' : 'Izaberite opciju';
        }
      }

      disableWithMessage(message) {
        if (!this.addButton || !this.addButtonText) return;
        this.addButton.disabled = true;
        this.addButtonText.textContent = message;
      }

      async handleAddToCart(event) {
        event.preventDefault();

        if (!this.selectedVariant?.available || this.addButton.disabled) return;

        this.clearError();
        this.setLoadingState(true);

        const formData = new FormData();
        formData.append('id', String(this.selectedVariant.id));
        formData.append('quantity', '1');
        formData.append('sections', 'cart-drawer,cart-icon-bubble');
        formData.append('sections_url', window.location.pathname);

        try {
          const endpoint = window.routes?.cart_add_url || '/cart/add.js';

          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: formData,
          });

          const data = await response.json();

          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || 'Cart add failed');
          }

          this.updateCartSections(data);
          this.publishCartUpdate(data);
          this.showAddedState();
        } catch (error) {
          console.error('Armonia add to cart failed.', error);
          this.showError('Proizvod trenutno nije moguće dodati u korpu. Pokušajte ponovo.');
          this.setButtonState('ready');
        } finally {
          this.setLoadingState(false);
        }
      }

      setLoadingState(isLoading) {
        if (!this.addButton || !this.addButtonText) return;

        this.addButton.classList.toggle('is-loading', isLoading);
        this.addButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');

        if (isLoading) {
          this.addButton.disabled = true;
          this.addButtonText.textContent = 'Dodavanje...';
        } else if (!this.addButton.classList.contains('is-added')) {
          if (this.selectedVariant?.available) {
            this.setButtonState('ready');
          }
        }
      }

      showAddedState() {
        if (!this.addButton || !this.addButtonText) return;

        this.addButton.classList.remove('is-loading');
        this.addButton.classList.add('is-added');
        this.addButton.disabled = false;
        this.addButtonText.textContent = 'Dodato ✓';

        window.clearTimeout(this.addedStateTimer);

        this.addedStateTimer = window.setTimeout(() => {
          this.addButton.classList.remove('is-added');

          if (this.selectedVariant?.available) {
            this.setButtonState('ready');
          }
        }, 1400);
      }

      updateCartSections(data) {
        if (!data?.sections) return;

        this.updateCartIcon(data.sections['cart-icon-bubble']);
        this.updateCartDrawer(data.sections['cart-drawer']);
      }

      updateCartIcon(sectionHtml) {
        if (!sectionHtml) return;

        const currentIcon = document.querySelector('#cart-icon-bubble');
        if (!currentIcon) return;

        const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const nextIcon = parsed.querySelector('#cart-icon-bubble');

        if (nextIcon) {
          currentIcon.innerHTML = nextIcon.innerHTML;
        }
      }

      updateCartDrawer(sectionHtml) {
        if (!sectionHtml) return;

        const drawer = document.querySelector('cart-drawer');
        const currentDrawer = document.querySelector('#CartDrawer');

        if (!drawer || !currentDrawer) return;

        const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const nextDrawer = parsed.querySelector('#CartDrawer');

        if (!nextDrawer) return;

        currentDrawer.innerHTML = nextDrawer.innerHTML;

        drawer.classList.remove('is-empty');
        currentDrawer.classList.remove('is-empty');

        const overlay = drawer.querySelector('#CartDrawer-Overlay');

        if (overlay && typeof drawer.close === 'function') {
          overlay.addEventListener('click', drawer.close.bind(drawer));
        }
      }

      publishCartUpdate(data) {
        try {
          if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'armonia-card-purchase',
            productVariantId: this.selectedVariant?.id,
            cartData: data,
          });
        } catch (error) {
          console.warn('Armonia cart update event could not be published.', error);
        }
      }

      showError(message) {
        if (!this.errorMessage) return;

        this.errorMessage.textContent = message;
        this.errorMessage.hidden = false;
      }

      clearError() {
        if (!this.errorMessage) return;

        this.errorMessage.textContent = '';
        this.errorMessage.hidden = true;
      }
    },
  );
}
